/**
 * delivery-saas-api 택배 특수 기능 서비스
 * UI에서 직접 import하여 사용 (Store 팩토리와 분리)
 */

import { useApi } from '@/composables/useApi'
import type { ApiResponse } from '@farmfree/common/types/apiResponse'
import { withApiSafety } from '@/utils/withApiSafety'
import type { AccountInquiryResponse } from '@farmfree/common/types/courierSaas/accountInquiry'
import type {
  AccountTrackingBatchResponse,
  AccountTrackingItem,
} from '@farmfree/common/types/courierSaas/accountTracking'
import type { BulkUploadItem } from '@farmfree/common/types/courierSaas/data'
import type { BulkUploadResponse } from '@farmfree/common/types/courierSaas/bulkUpload'
import type {
  UpdateTrackingNumbersRequest,
  UpdateTrackingNumbersResponse,
} from '@farmfree/common/types/tracking/updateTrackingNumbers'
import type { LoginResult } from '@farmfree/common/types/courierSaas/api'
import type { CourierViewScope } from '@farmfree/common/types/courierSaas/courierSaasAccount'

// OTP 검증 결과는 LoginResult와 동일한 구조
export type OtpVerifyResult = LoginResult

export interface RegisterAccountParams {
  companyId: string
  providerId: string
  accountId: string
  accountPassword: string
}

export interface VerifyOtpParams {
  companyId: string
  courierId: string
  sessionId: string
  otpCode: string
  providerId: string
  accountId?: string // Optional - 서버가 DB에서 조회
  accountPassword?: string // Optional - 서버가 DB에서 조회
}

export interface InquiryParams {
  companyId: string
  courierId: string
  fromDate: string // YYYYMMDD
  toDate: string // YYYYMMDD
  ownerPhone?: string // 관리자 핸드폰 번호 (viewScope가 'own_phone_only'일 때 사용)
}

export interface BulkUploadParams {
  companyId: string
  courierId: string
  items: BulkUploadItem[]
}

export interface DashboardStatsParams {
  companyId: string
  courierId: string
  fromDate: string // YYYYMMDD
  toDate: string // YYYYMMDD
}

export interface TrackParams {
  companyId: string
  courierId: string
  trackingNumbers: string[] // 송장번호 배열
  includeDetails?: boolean // 상세 정보 포함 여부 (기본값: false)
  excludeHistoryData?: boolean // movements, updateHistory 제외 여부 (기본값: false)
}

export interface GetPrintDataParams {
  companyId: string
  courierId: string
  courierTrackingIds: string[] // 택배사 관리번호 배열 (rsrvMgrNo 등)
}

export interface OzViewerConfig {
  wsUrl: string
  reportServer: string
  reportName: string
  namespace: string
  downloadServer: string
  downloadPort: number
  downloadInstruction: string
  version: number
}

export interface PrintDataResponse {
  items: Record<string, unknown>[]
  ozConfig: OzViewerConfig
}

export interface GetInvPrintDefInfosParams {
  companyId: string
  courierId: string
  courierTrackingIds: string[] // 택배사 관리번호 배열 (rsrvMgrNo 등)
  invKndCd?: string // 운송장 종류 코드 (600: 표준라벨D, 601: 표준라벨E, ...)
  prntYn?: 'Y' | 'N' // 출력 여부 (Y: 실제 출력 - 송장번호 발급, N: 미리보기 - 송장번호 발급 안함)
}

export interface LotteLabelPosition {
  fieldName: string
  reportName: string
  lnbCd: string
  bdrCd: string | null
  h: number
  fontStyle: string
  itmAreaSctCd: string | null
  w: number
  x: number
  y: number
  fontSize: number
  ybnk: number
  xbnk: number
}

export interface InvPrintDefInfosResponse {
  success: boolean
  errorMessage?: string
  content: Array<Record<string, unknown>> // 출력용 상세 데이터
  ozConfig: OzViewerConfig
  labelNames: Array<{ labelName: string }> // OZ 뷰어 출력용 라벨 필드명
  invKndCd: string // 운송장 종류 코드
  position: LotteLabelPosition[] // OZ 뷰어 라벨 위치 정보
}

export interface PrintAfterProcessParams {
  companyId: string
  courierId: string
  courierTrackingIds: string[] // 택배사 관리번호 배열 (rsrvMgrNo 등)
  trackingNumbers: string[] // 출력된 송장번호 배열
}

export interface PrintAfterProcessResponse {
  success: boolean
  errorMessage?: string
}

export interface CancelPrintParams {
  companyId: string
  courierId: string
  rsrvInfoList: Array<Record<string, unknown>> // invprntdefinfos 응답의 content 데이터
}

export interface CancelPrintResponse {
  success: boolean
  errorMessage?: string
}

/**
 * 택배 특수 기능 서비스 팩토리
 * 계정 등록, 조회, 대량 업로드 등 CRUD 외의 기능 제공
 */
export function createCourierSaasFunctionsService() {
  return {
    /**
     * OTP 검증 (2FA 2단계)
     * POST /api/courier-saas/verify-otp
     */
    async verifyOtp(params: VerifyOtpParams): Promise<ApiResponse<OtpVerifyResult>> {
      const api = useApi()
      return withApiSafety(() =>
        api.post<ApiResponse<OtpVerifyResult>>('/api/courier-saas/verify-otp', params)
      )
    },

    /**
     * 택배사 계정 등록
     * POST /api/courier-saas/register-account
     *
     * @returns 성공 시: { courierAccountKey: string, courierId: string }
     *          OTP 필요 시: { requiresOtp: boolean, sessionId?: string, message?: string, courierId?: string }
     */
    async registerAccount(
      params: RegisterAccountParams
    ): Promise<
      ApiResponse<
        | { courierAccountKey: string; courierId: string }
        | { requiresOtp: boolean; sessionId?: string; message?: string; courierId?: string }
      >
    > {
      const api = useApi()
      return withApiSafety(() =>
        api.post<
          ApiResponse<
            | { courierAccountKey: string; courierId: string }
            | { requiresOtp: boolean; sessionId?: string; message?: string; courierId?: string }
          >
        >('/api/courier-saas/register-account', params)
      )
    },

    /**
     * 택배 조회 (배송 현황)
     * POST /api/courier-saas/inquiry
     */
    async inquireOrders(params: InquiryParams): Promise<ApiResponse<AccountInquiryResponse>> {
      const api = useApi()
      return withApiSafety(() =>
        api.post<ApiResponse<AccountInquiryResponse>>('/api/courier-saas/inquiry', params)
      )
    },

    /**
     * 택배 대량 등록
     * POST /api/courier-saas/bulk-upload
     */
    async bulkUpload(params: BulkUploadParams): Promise<ApiResponse<BulkUploadResponse>> {
      const api = useApi()
      return withApiSafety(() =>
        api.post<ApiResponse<BulkUploadResponse>>('/api/courier-saas/bulk-upload', params)
      )
    },

    /**
     * 대시보드 통계 조회
     * POST /api/courier-saas/dashboard
     */
    async getDashboardStats(params: DashboardStatsParams): Promise<ApiResponse<any>> {
      const api = useApi()
      return withApiSafety(() => api.post<ApiResponse<any>>('/api/courier-saas/dashboard', params))
    },

    /**
     * 배송 추적 (송장번호 배열)
     * POST /api/courier-saas/track
     */
    async trackOrders(params: TrackParams): Promise<ApiResponse<AccountTrackingBatchResponse>> {
      const api = useApi()
      return withApiSafety(() =>
        api.post<ApiResponse<AccountTrackingBatchResponse>>('/api/courier-saas/track', params)
      )
    },

    /**
     * 송장번호 업데이트 (택배사에서 배송 정보 조회 후 OrderPen 업데이트)
     * POST /api/courier-saas/update-tracking-numbers
     */
    async updateTrackingNumbers(
      params: UpdateTrackingNumbersRequest
    ): Promise<ApiResponse<UpdateTrackingNumbersResponse>> {
      const api = useApi()
      return withApiSafety(() =>
        api.post<ApiResponse<UpdateTrackingNumbersResponse>>(
          '/api/courier-saas/update-tracking-numbers',
          params
        )
      )
    },

    /**
     * CJ 엑셀 양식 정보 조회
     * POST /api/courier-saas/fetch-layout-info
     */
    async fetchLayoutInfo(params: {
      companyId: string
      courierId: string
      typSn?: string
    }): Promise<ApiResponse<any>> {
      const api = useApi()
      return withApiSafety(() =>
        api.post<ApiResponse<any>>('/api/courier-saas/fetch-layout-info', params)
      )
    },

    /**
     * CJ 계정별 양식 목록 조회 (저장 없이 목록만 반환)
     * POST /api/courier-saas/fetch-layout-list
     */
    async fetchLayoutList(params: {
      companyId: string
      courierId: string
    }): Promise<ApiResponse<{ layoutList: any[] }>> {
      const api = useApi()
      return withApiSafety(() =>
        api.post<ApiResponse<{ layoutList: any[] }>>('/api/courier-saas/fetch-layout-list', params)
      )
    },

    /**
     * CJ 양식 필드 정보 조회 (저장 없이 필드만 반환)
     * POST /api/courier-saas/fetch-layout-fields
     */
    async fetchLayoutFields(params: {
      companyId: string
      courierId: string
      typSn: string
    }): Promise<ApiResponse<{ fields: any[] }>> {
      const api = useApi()
      return withApiSafety(() =>
        api.post<ApiResponse<{ fields: any[] }>>('/api/courier-saas/fetch-layout-fields', params)
      )
    },

    /**
     * CJ 레이아웃 필드 매핑 저장
     * POST /api/courier-saas/save-layout-config
     */
    async saveLayoutConfig(params: {
      companyId: string
      courierId: string
      typSn: string
      typNm: string
      fieldMapping: any[]
    }): Promise<ApiResponse<{ layoutConfig: any }>> {
      const api = useApi()
      return withApiSafety(() =>
        api.post<ApiResponse<{ layoutConfig: any }>>('/api/courier-saas/save-layout-config', params)
      )
    },

    /**
     * CJ 레이아웃 필드 매핑 조회
     * GET /api/courier-saas/layout-config?companyId=xxx&courierId=xxx
     */
    async getLayoutConfig(params: {
      companyId: string
      courierId: string
    }): Promise<ApiResponse<{ layoutConfig: any | null }>> {
      const api = useApi()
      return withApiSafety(() =>
        api.get<ApiResponse<{ layoutConfig: any | null }>>(
          `/api/courier-saas/layout-config?companyId=${params.companyId}&courierId=${params.courierId}`
        )
      )
    },

    /**
     * CJ 레이아웃 필드 매핑 삭제
     * DELETE /api/courier-saas/layout-config
     */
    async deleteLayoutConfig(params: {
      companyId: string
      courierId: string
    }): Promise<ApiResponse<void>> {
      const api = useApi()
      return withApiSafety(() =>
        api.delete<ApiResponse<void>>('/api/courier-saas/layout-config', { data: params })
      )
    },

    /**
     * 업로드 이력 목록 조회
     */
    async getBulkUploadHistories(params: {
      companyId: string
      courierId: string
      providerId: string
      limit?: number
    }): Promise<ApiResponse<{ histories: any[] }>> {
      const api = useApi()
      return withApiSafety(() =>
        api.get<ApiResponse<{ histories: any[] }>>('/api/courier-saas/bulk-upload-histories', {
          params,
        })
      )
    },

    /**
     * 업로드 이력 상세 조회
     */
    async getBulkUploadHistoryDetail(params: {
      companyId: string
      courierId: string
      historyId: string
    }): Promise<ApiResponse<{ history: any; detailItems: any[] }>> {
      const api = useApi()
      return withApiSafety(() =>
        api.get<ApiResponse<{ history: any; detailItems: any[] }>>(
          `/api/courier-saas/bulk-upload-histories/${params.historyId}/detail`,
          {
            params: {
              companyId: params.companyId,
              courierId: params.courierId,
            },
          }
        )
      )
    },

    /**
     * 업로드 삭제
     */
    async deleteBulkUploadHistory(params: {
      companyId: string
      courierId: string
      historyId: string
    }): Promise<ApiResponse<void>> {
      const api = useApi()
      return withApiSafety(() =>
        api.delete<ApiResponse<void>>(
          `/api/courier-saas/bulk-upload-histories/${params.historyId}`,
          {
            data: {
              companyId: params.companyId,
              courierId: params.courierId,
            },
          }
        )
      )
    },

    /**
     * 택배 계정 강제 재로그인 (DB 저장된 ID/PW로 자동 로그인)
     * POST /api/courier-saas/force-reauth
     *
     * 이중 로그인 불가 택배사(CJ 등)에서 사용자가 명시적으로 재로그인 버튼을 누를 때 사용
     */
    async forceReauth(params: {
      companyId: string
      courierId: string
    }): Promise<
      ApiResponse<
        | { message: string; tokenSource: string }
        | { requiresOtp: boolean; sessionId?: string; message?: string }
      >
    > {
      const api = useApi()
      return withApiSafety(() =>
        api.post<
          ApiResponse<
            | { message: string; tokenSource: string }
            | { requiresOtp: boolean; sessionId?: string; message?: string }
          >
        >('/api/courier-saas/force-reauth', params)
      )
    },

    /**
     * 택배 계정 권한 설정 업데이트
     * POST /api/courier-saas/update-account-permissions
     *
     * @param params.canRegister - 등록 권한 (true: 등록 가능, false: 조회만 가능)
     * @param params.viewScope - 조회 범위 ('all': 전체, 'own_phone_only': 본인 발신건만)
     */
    async updateAccountPermissions(params: {
      companyId: string
      courierId: string
      canRegister: boolean
      viewScope: CourierViewScope
    }): Promise<ApiResponse<{ message: string }>> {
      const api = useApi()
      return withApiSafety(() =>
        api.post<ApiResponse<{ message: string }>>(
          '/api/courier-saas/update-account-permissions',
          params
        )
      )
    },

    /**
     * 송장 출력 데이터 조회 (출력 가능 여부 확인)
     * POST /api/courier-saas/print-data
     *
     * 택배사에서 송장 출력에 필요한 데이터와 OZ Viewer 설정을 조회합니다.
     * 롯데택배의 경우 courierTrackingId = rsrvMgrNo (관리번호)
     *
     * 이 API는 출력 가능 여부만 확인합니다 (prntbfchck).
     * 실제 출력 데이터는 getInvPrintDefInfos를 사용하세요.
     */
    async getPrintData(params: GetPrintDataParams): Promise<ApiResponse<PrintDataResponse>> {
      const api = useApi()
      return withApiSafety(() =>
        api.post<ApiResponse<PrintDataResponse>>('/api/courier-saas/print-data', params)
      )
    },

    /**
     * 송장 출력 상세 데이터 조회 (invprntdefinfos)
     * POST /api/courier-saas/inv-print-def-infos
     *
     * getPrintData(prntbfchck)로 출력 가능 여부 확인 후,
     * 이 API로 실제 출력용 상세 데이터를 조회합니다.
     *
     * 롯데택배의 경우 courierTrackingId = rsrvMgrNo (관리번호)
     */
    async getInvPrintDefInfos(
      params: GetInvPrintDefInfosParams
    ): Promise<ApiResponse<InvPrintDefInfosResponse>> {
      const api = useApi()
      return withApiSafety(() =>
        api.post<ApiResponse<InvPrintDefInfosResponse>>(
          '/api/courier-saas/inv-print-def-infos',
          params
        )
      )
    },

    /**
     * 출력 완료 후처리 (invprntafprcss)
     * POST /api/courier-saas/print-after-process
     *
     * 출력 완료 후 택배사에 알림
     */
    async printAfterProcess(
      params: PrintAfterProcessParams
    ): Promise<ApiResponse<PrintAfterProcessResponse>> {
      const api = useApi()
      return withApiSafety(() =>
        api.post<ApiResponse<PrintAfterProcessResponse>>(
          '/api/courier-saas/print-after-process',
          params
        )
      )
    },

    /**
     * 출력 취소 (updrsrvinvno)
     * POST /api/courier-saas/cancel-print
     *
     * OZ Viewer 프린터 제어 팝업에서 취소 시 발급된 송장번호 회수
     */
    async cancelPrint(params: CancelPrintParams): Promise<ApiResponse<CancelPrintResponse>> {
      const api = useApi()
      return withApiSafety(() =>
        api.post<ApiResponse<CancelPrintResponse>>('/api/courier-saas/cancel-print', params)
      )
    },
  }
}

export type CourierSaasFunctionsService = ReturnType<typeof createCourierSaasFunctionsService>
